import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { CardGridSkeleton } from '../components/PageSkeletons';
import { PullToRefresh } from '../components/PullToRefresh';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search, 
  Trophy,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Heart,
  HeartOff,
  Bell
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];

export const Teams = () => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [followedTeams, setFollowedTeams] = useState(new Set());

  useEffect(() => {
    fetchTeams();
    if (user) {
      fetchFollowedTeams();
    }
  }, [user]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowedTeams = async () => {
    try {
      const response = await axios.get(`${API}/following/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowedTeams(new Set(response.data.map(f => f.team_id)));
    } catch (error) {
      console.error('Failed to fetch followed teams:', error);
    }
  };

  const toggleFollow = async (teamId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to follow teams');
      return;
    }

    try {
      if (followedTeams.has(teamId)) {
        await axios.delete(`${API}/teams/${teamId}/follow`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowedTeams(prev => {
          const next = new Set(prev);
          next.delete(teamId);
          return next;
        });
        toast.success('Unfollowed team');
      } else {
        await axios.post(`${API}/teams/${teamId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFollowedTeams(prev => new Set([...prev, teamId]));
        toast.success('Now following team! You will get notifications for their games.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow status');
    }
  };

  const filteredTeams = teams.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
                          t.coach_name.toLowerCase().includes(search.toLowerCase());
    const matchesAge = ageFilter === 'All' || t.age_category === ageFilter;
    const matchesGender = genderFilter === 'All' || t.gender === genderFilter;
    return matchesSearch && matchesAge && matchesGender;
  });

  const getWinRate = (wins, losses) => {
    const total = wins + losses;
    if (total === 0) return 0;
    return Math.round((wins / total) * 100);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CardGridSkeleton count={6} />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchTeams}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="teams-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">{t('team.title').toUpperCase()}</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('team.browseDescription') || 'Browse registered teams and their stats'}
          </p>
        </div>
        
        {user && (
          <Link to="/teams/create" className="mt-4 md:mt-0">
            <Button className="btn-primary gap-2" data-testid="create-team-btn">
              <Plus className="w-4 h-4" />
              {t('team.createTeam')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('team.searchPlaceholder') || "Search teams or coaches..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="age-filter">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Teams Grid */}
      {filteredTeams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team, index) => (
            <Card 
              key={team.id} 
              className="card-hover"
              data-testid={`team-card-${index}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {team.logo_url ? (
                      <img 
                        src={team.logo_url} 
                        alt={team.name} 
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Users className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <Badge variant="secondary">
                      {team.age_category} • {team.gender}
                    </Badge>
                  </div>
                  <span className="font-mono text-sm">
                    {getWinRate(team.wins, team.losses)}% Win Rate
                  </span>
                </div>
                <CardTitle className="text-xl font-bold mt-2">{team.name}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Coach: {team.coach_name}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-green-500/10">
                    <TrendingUp className="w-4 h-4 mx-auto text-green-500 mb-1" />
                    <p className="text-2xl font-bold text-green-500">{team.wins}</p>
                    <p className="text-xs text-muted-foreground">Wins</p>
                  </div>
                  <div className="p-3 rounded-lg bg-red-500/10">
                    <TrendingDown className="w-4 h-4 mx-auto text-red-500 mb-1" />
                    <p className="text-2xl font-bold text-red-500">{team.losses}</p>
                    <p className="text-xs text-muted-foreground">Losses</p>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Trophy className="w-4 h-4 mx-auto text-primary mb-1" />
                    <p className="text-2xl font-bold text-primary">{team.tournaments_played?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Tourneys</p>
                  </div>
                </div>

                {/* Points */}
                <div className="flex justify-between text-sm p-3 rounded-lg bg-muted/50">
                  <div>
                    <span className="text-muted-foreground">Points For: </span>
                    <span className="font-bold text-green-500">{team.points_scored}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Points Against: </span>
                    <span className="font-bold text-red-500">{team.points_allowed}</span>
                  </div>
                </div>

                {/* Players count */}
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{team.players?.length || 0} players</span>
                  </div>
                  {user && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => toggleFollow(team.id, e)}
                      className={followedTeams.has(team.id) ? 'text-red-500' : 'text-muted-foreground'}
                      data-testid={`follow-team-${index}`}
                    >
                      {followedTeams.has(team.id) ? (
                        <>
                          <Heart className="w-4 h-4 mr-1 fill-current" />
                          Following
                        </>
                      ) : (
                        <>
                          <HeartOff className="w-4 h-4 mr-1" />
                          Follow
                        </>
                      )}
                    </Button>
                  )}
                </div>

                <Link to={`/teams/${team.id}`}>
                  <Button className="w-full gap-2" variant="outline" data-testid={`view-team-${index}`}>
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12" data-testid="no-teams">
          <CardContent>
            <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Teams Found</h3>
            <p className="text-muted-foreground mb-6">
              {search || ageFilter !== 'All' || genderFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Be the first to register a team'}
            </p>
            {user && (
              <Link to="/teams/create">
                <Button className="btn-primary gap-2">
                  <Plus className="w-4 h-4" />
                  Register Team
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </PullToRefresh>
  );
};
